import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import { useNavigate, useMatch } from "react-router-dom";
import { addTab, removeTab, useTabs, TAB_TYPES } from "slices/tabsSlice";
import NominalBalances from "pages/Reports/NominalBalaces";
import SupplierBalances from "pages/Reports/SupplierBalance";
import MatterBalances from "pages/Reports/MatterBalances";
import { Tabs } from "components/Enums/TabEnums.ts";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import AccessRevokedModal from "components/common/AccessRevokedModal";
import ClientListing from "pages/Reports/ClientListing";
import BillingReport from "pages/Reports/BillingReports";
import Disbursements from "pages/Reports/DisbursementReport";
import BankRec from "pages/Reports/BankRec";
import { useReportPermissions } from "hooks/useReportPermissions";

const ReportTabs = React.memo((props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [selected, setSelected] = useState(-1);

  const tabsData = useTabs(TAB_TYPES.REPORTS);
  const { allowed } = useAllowedLiscence(Tabs.REPORTS);
  const { reportPermissions } = useReportPermissions();

  const reportPermissionMap = {
    supplierbalances: "SupplierBalance",
    nominalbalances: "NominalBalance",
    matterbalances: "MatterBalances",
    clientlisting: "ClientListing",
    disbursements: "Disbursements",
    billing: "Billing",
    bankrec: "BankRec",
  };

  const findContent = (item) => {
    switch (item) {
      case "supplierbalances":
        return "Supplier Balances";
      case "nominalbalances":
        return "Nominal Balances";
      case "matterbalances":
        return "Matter Balances";
      case "clientlisting":
        return "Client Listing";
      case "disbursements":
        return "Disbursements";
      case "billing":
        return "Billing";
      case "bankrec":
        return "Bank Rec";
    }
  };

  const hasAccessToReport = React.useCallback(
    (reportRoute) => {
      const permissionKey = reportPermissionMap[reportRoute?.toLowerCase()];
      return permissionKey ? reportPermissions[permissionKey] === true : false;
    },
    [reportPermissions],
  );

  const urlDefiner = (e) => {
    if (params !== null) {
      let url = tabs[e]?.toLowerCase();
      if (url === undefined) {
        url = "";
      }
      navigate(`/reports/${url}`);
    }
  };

  const params = useMatch("reports/:data");
  useEffect(() => {
    if (params !== null) {
      let data = params?.params?.data;
      const reportRoute = data?.toLowerCase();

      if (!hasAccessToReport(reportRoute)) {
        const accessibleReports = Object.keys(reportPermissionMap).filter((route) =>
          hasAccessToReport(route),
        );

        if (accessibleReports.length > 0) {
          navigate(`/reports/${accessibleReports[0]}`);
        } else {
          navigate("/dashboard");
        }
        return;
      }

      if (!tabsData.includes(reportRoute)) {
        dispatch(addTab("reports", reportRoute));
      } else {
        setSelected(tabsData.indexOf(reportRoute));
      }
    }
  }, [params, tabsData, reportPermissions, hasAccessToReport]);

  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData);
      const segment = params?.params?.data?.toLowerCase();
      const index = segment ? tabsData.indexOf(segment) : -1;
      setSelected(index === -1 ? 0 : index);
    }
  }, [tabsData, params?.params?.data]);

  const removeTabDispatcher = (tab) => {
    const closedIndex = tabs.findIndex((t) => findContent(t) === tab);
    if (closedIndex === -1) return;

    const closedTabKey = tabs[closedIndex];
    const dispatchRemove = () => {
      queueMicrotask(() => {
        dispatch(removeTab("reports", closedTabKey));
      });
    };

    const newTabs = tabs.filter((t) => findContent(t) !== tab);
    if (!newTabs.length) {
      setSelected(0);
      props.closeTab("Reports");
      dispatchRemove();
      return;
    }
    const newSelected =
      closedIndex === selected
        ? Math.min(selected, newTabs.length - 1)
        : closedIndex < selected
          ? selected - 1
          : selected;
    setSelected(newSelected);
    navigate(`/reports/${newTabs[newSelected]?.toLowerCase() ?? ""}`);
    dispatchRemove();
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
    urlDefiner(e.selected);
  };

  return allowed ? (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      keepTabsMounted={true}
      className="navigationTabs">
      {tabs.map((item) => {
        if (!hasAccessToReport(item)) {
          return null;
        }

        return (
          item && (
            <TabStripTab
              title={
                <Title
                  content={findContent(item)}
                  onTabRemove={removeTabDispatcher}
                  selected={findContent(tabs.at(selected))}
                  count={tabs.length}
                />
              }
              key={item}>
              <div>
                {item === "supplierbalances" &&
                hasAccessToReport("supplierbalances") ? (
                  <SupplierBalances />
                ) : (
                  ""
                )}
                {item === "nominalbalances" &&
                hasAccessToReport("nominalbalances") ? (
                  <NominalBalances />
                ) : (
                  ""
                )}
                {item === "matterbalances" && hasAccessToReport("matterbalances") ? (
                  <MatterBalances />
                ) : (
                  ""
                )}
                {item === "clientlisting" && hasAccessToReport("clientlisting") ? (
                  <ClientListing />
                ) : (
                  ""
                )}
                {item === "billing" && hasAccessToReport("billing") ? (
                  <BillingReport />
                ) : (
                  ""
                )}
                {item === "disbursements" && hasAccessToReport("disbursements") ? (
                  <Disbursements />
                ) : (
                  ""
                )}
                {item === "bankrec" && hasAccessToReport("bankrec") ? (
                  <BankRec />
                ) : (
                  ""
                )}
              </div>
            </TabStripTab>
          )
        );
      })}
    </TabStrip>
  ) : (
    <AccessRevokedModal removeTab={() => props.closeTab()} />
  );
});

export default ReportTabs;
